/**
 * @fileoverview AR-924 — Big drop target of step 1 with "Browse files", next to the matching tip card.
 */

import { Button, Group, Paper, Stack, Text, ThemeIcon, useMantineTheme } from '@mantine/core'
import { IconFolder, IconUpload, IconBulb } from '@tabler/icons-react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { MAX_FILE_SIZE_MB } from '../teacherUpload'

const CLOUD_DISC_SIZE = 44
const CLOUD_ICON_SIZE = 20
const BUTTON_ICON_SIZE = 16
const TIP_ICON_SIZE = 16
/** Figma: tip card is a fixed 300px column next to the drop zone. */
const TIP_WIDTH = 300
const IDLE_DASH_ALPHA = 0.5
const TIP_TITLE_GAP = 6
const EXAMPLE_PADDING_Y = 6

interface ClassDropzoneProps {
  readonly disabled: boolean
  readonly onFiles: (files: File[]) => void
}

export function ClassDropzone({ disabled, onFiles }: ClassDropzoneProps) {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const tr = (key: string, options?: object) => t(`task_tracker.upload_for_class.${key}`, options)
  // No `accept`/`maxSize` on purpose: every file is staged by `stageFile`, so rejects show as failed rows.
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: onFiles, disabled, noClick: true, noKeyboard: true,
  })

  return (
    <Group align="stretch" spacing="md" noWrap>
      <Paper
        {...getRootProps()}
        radius="md"
        py="md"
        px="lg"
        sx={theme => {
          const brand = theme.fn.variant({ variant: 'light', color: theme.primaryColor })
          const primary = theme.fn.primaryColor()
          const dashColor = isDragActive ? primary : theme.fn.rgba(primary, IDLE_DASH_ALPHA)
          return {
            flex: 1,
            border: `2px dashed ${dashColor}`,
            background: brand.background,
            textAlign: 'center',
          }
        }}
      >
        <input {...getInputProps()} />
        <Stack spacing="xs" align="center">
          <ThemeIcon variant="outline" radius="xl" size={CLOUD_DISC_SIZE}>
            <IconUpload size={CLOUD_ICON_SIZE} />
          </ThemeIcon>
          <Text size="sm" weight={700}>{tr('drop_title')}</Text>
          <Text size="xs" color="dimmed">{tr('drop_hint', { mb: MAX_FILE_SIZE_MB })}</Text>
          <Button
            variant="outline"
            leftIcon={<IconFolder size={BUTTON_ICON_SIZE} />}
            onClick={open}
            disabled={disabled}
          >
            {tr('browse')}
          </Button>
        </Stack>
      </Paper>

      <Paper
        withBorder
        radius="md"
        p="md"
        sx={theme => ({ width: TIP_WIDTH, flexShrink: 0, background: theme.colors.gray[0] })}
      >
        <Stack spacing="xs">
          <Group spacing={TIP_TITLE_GAP} noWrap>
            <IconBulb size={TIP_ICON_SIZE} color={theme.fn.primaryColor()} />
            <Text size="sm" weight={700}>{tr('tip_title')}</Text>
          </Group>
          <Text size="xs" color="dimmed">{tr('tip_body')}</Text>
          <Paper radius="sm" px="sm" py={EXAMPLE_PADDING_Y} sx={theme => ({ background: theme.colors.gray[2] })}>
            <Text size="xs">andrew_moore.jpg</Text>
            <Text size="xs">autumn-davis-p1.jpg</Text>
          </Paper>
          <Text size="xs" color="dimmed">{tr('tip_footer')}</Text>
        </Stack>
      </Paper>
    </Group>
  )
}
